import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, subDays, format } from 'date-fns';

export interface DateRange {
  from: Date;
  to: Date;
}

export interface StandardViewStat {
  slug: string;
  title: string;
  views: number;
}

export interface AdfElementStat {
  slug: string;
  views: number;
}

export interface DocViewStat {
  slug: string;
  docType: string;
  views: number;
}

export interface ValidationStat {
  slug: string;
  title: string;
  validations: number;
  successRate: number;
}

export interface ExportFormatStat {
  format: string;
  count: number;
}

export interface SearchTermStat {
  term: string;
  count: number;
}

export interface ApiUsageStat {
  endpoint: string;
  count: number;
  successRate: number;
}

export interface DailyActivityStat {
  date: string;
  views: number;
  validations: number;
  exports: number;
  adfViews: number;
  docViews: number;
  builderActions: number;
}

export interface BuilderUsageStat {
  builder: string;
  actions: number;
}

export interface AnalyticsSummary {
  totalViews: number;
  totalValidations: number;
  totalExports: number;
  totalSearches: number;
  totalAdfViews: number;
  totalDocViews: number;
  totalBuilderActions: number;
  uniqueSessions: number;
}

export interface PeriodComparison {
  viewsChange: number;
  validationsChange: number;
  exportsChange: number;
  searchesChange: number;
  sessionsChange: number;
}

export function useStandardsAnalytics(dateRange: DateRange) {
  const fromDate = format(startOfDay(dateRange.from), "yyyy-MM-dd'T'HH:mm:ss");
  const toDate = format(startOfDay(subDays(dateRange.to, -1)), "yyyy-MM-dd'T'HH:mm:ss");

  // Calculate previous period for comparison
  const periodDays = Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24));
  const prevFromDate = format(startOfDay(subDays(dateRange.from, periodDays)), "yyyy-MM-dd'T'HH:mm:ss");
  const prevToDate = format(startOfDay(dateRange.from), "yyyy-MM-dd'T'HH:mm:ss");

  // Fetch raw analytics events for current period
  const { data: rawEvents, isLoading } = useQuery({
    queryKey: ['analytics-events', fromDate, toDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('analytics_events')
        .select('event_type, event_action, standard_slug, metadata, created_at, session_id')
        .gte('created_at', fromDate)
        .lte('created_at', toDate)
        .order('created_at', { ascending: false })
        .limit(10000);

      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch previous period events for comparison
  const { data: prevEvents } = useQuery({
    queryKey: ['analytics-events-prev', prevFromDate, prevToDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('analytics_events')
        .select('event_type, event_action, session_id')
        .gte('created_at', prevFromDate)
        .lt('created_at', prevToDate)
        .limit(10000);

      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch standards for title lookup
  const { data: standards } = useQuery({
    queryKey: ['standards-lookup'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('data_standards')
        .select('slug, title')
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const standardsMap = new Map(standards?.map(s => [s.slug, s.title]) || []);

  // Process raw events into aggregated stats
  const processedData = rawEvents ? processEvents(rawEvents as RawEvent[], standardsMap, dateRange) : null;
  
  // Calculate period comparison
  const comparison = calculateComparison(rawEvents as RawEvent[] | undefined, prevEvents as RawEvent[] | undefined);

  return {
    isLoading,
    summary: processedData?.summary || { 
      totalViews: 0, 
      totalValidations: 0, 
      totalExports: 0, 
      totalSearches: 0,
      totalAdfViews: 0,
      totalDocViews: 0,
      totalBuilderActions: 0,
      uniqueSessions: 0,
    },
    comparison: comparison || {
      viewsChange: 0,
      validationsChange: 0,
      exportsChange: 0,
      searchesChange: 0,
      sessionsChange: 0,
    },
    topViewedStandards: processedData?.topViewedStandards || [],
    topValidatedStandards: processedData?.topValidatedStandards || [],
    exportsByFormat: processedData?.exportsByFormat || [],
    topSearchTerms: processedData?.topSearchTerms || [],
    apiUsageByEndpoint: processedData?.apiUsageByEndpoint || [],
    dailyActivity: processedData?.dailyActivity || [],
    topAdfElements: processedData?.topAdfElements || [],
    topDocArticles: processedData?.topDocArticles || [],
    builderUsage: processedData?.builderUsage || [],
  };
}

interface RawEvent {
  event_type: string;
  event_action: string;
  standard_slug: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  session_id?: string;
}

function calculateComparison(
  currentEvents: RawEvent[] | undefined,
  prevEvents: RawEvent[] | undefined
): PeriodComparison {
  if (!currentEvents || !prevEvents) {
    return { viewsChange: 0, validationsChange: 0, exportsChange: 0, searchesChange: 0, sessionsChange: 0 };
  }

  const countMetrics = (events: RawEvent[]) => {
    let views = 0, validations = 0, exports = 0, searches = 0;
    const sessions = new Set<string>();

    for (const event of events) {
      if (event.session_id) sessions.add(event.session_id);
      
      if (event.event_action === 'view') views++;
      else if (event.event_action === 'validate') validations++;
      else if (event.event_action === 'export') exports++;
      else if (event.event_type === 'search') searches++;
    }

    return { views, validations, exports, searches, sessions: sessions.size };
  };

  const current = countMetrics(currentEvents);
  const prev = countMetrics(prevEvents);

  const calcChange = (curr: number, prev: number) => {
    if (prev === 0) return curr > 0 ? 100 : 0;
    return Math.round(((curr - prev) / prev) * 100);
  };

  return {
    viewsChange: calcChange(current.views, prev.views),
    validationsChange: calcChange(current.validations, prev.validations),
    exportsChange: calcChange(current.exports, prev.exports),
    searchesChange: calcChange(current.searches, prev.searches),
    sessionsChange: calcChange(current.sessions, prev.sessions),
  };
}

function processEvents(
  events: RawEvent[],
  standardsMap: Map<string, string>,
  dateRange: DateRange
) {
  // Summary counts
  let totalViews = 0;
  let totalValidations = 0;
  let totalExports = 0;
  let totalSearches = 0;
  let totalAdfViews = 0;
  let totalDocViews = 0;
  let totalBuilderActions = 0;
  const uniqueSessions = new Set<string>();

  // Aggregation maps
  const viewsByStandard = new Map<string, number>();
  const validationsByStandard = new Map<string, { total: number; valid: number }>();
  const exportsByFormat = new Map<string, number>();
  const searchTerms = new Map<string, number>();
  const apiByEndpoint = new Map<string, { total: number; success: number }>();
  const activityByDate = new Map<string, { 
    views: number; 
    validations: number; 
    exports: number;
    adfViews: number;
    docViews: number;
    builderActions: number;
  }>();
  const adfElementViews = new Map<string, number>();
  const docViews = new Map<string, { docType: string; views: number }>();
  const builderActions = new Map<string, number>();

  // Initialize date range for daily activity
  const days = Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24));
  for (let i = 0; i <= days; i++) {
    const date = format(subDays(dateRange.to, days - i), 'yyyy-MM-dd');
    activityByDate.set(date, { views: 0, validations: 0, exports: 0, adfViews: 0, docViews: 0, builderActions: 0 });
  }

  // Process each event
  for (const event of events) {
    const dateKey = event.created_at.split('T')[0];
    const activity = activityByDate.get(dateKey) || { 
      views: 0, validations: 0, exports: 0, adfViews: 0, docViews: 0, builderActions: 0 
    };

    if (event.session_id) {
      uniqueSessions.add(event.session_id);
    }

    // API Standard events
    if (event.event_type === 'standard' || event.event_type === 'page_view') {
      if (event.event_action === 'view') {
        totalViews++;
        activity.views++;
        if (event.standard_slug) {
          viewsByStandard.set(
            event.standard_slug,
            (viewsByStandard.get(event.standard_slug) || 0) + 1
          );
        }
      } else if (event.event_action === 'validate') {
        totalValidations++;
        activity.validations++;
        if (event.standard_slug) {
          const existing = validationsByStandard.get(event.standard_slug) || { total: 0, valid: 0 };
          existing.total++;
          if (event.metadata?.valid) existing.valid++;
          validationsByStandard.set(event.standard_slug, existing);
        }
      } else if (event.event_action === 'export') {
        totalExports++;
        activity.exports++;
        const format = (event.metadata?.format as string) || 'unknown';
        exportsByFormat.set(format, (exportsByFormat.get(format) || 0) + 1);
      }
    } 
    // ADF events
    else if (event.event_type === 'adf') {
      if (event.event_action === 'view') {
        totalAdfViews++;
        activity.adfViews++;
        const elementSlug = event.metadata?.element_slug as string;
        if (elementSlug) {
          adfElementViews.set(elementSlug, (adfElementViews.get(elementSlug) || 0) + 1);
        }
      } else if (event.event_action === 'validate') {
        totalValidations++;
        activity.validations++;
      } else if (event.event_action === 'export') {
        totalExports++;
        activity.exports++;
        const format = (event.metadata?.format as string) || 'xml';
        exportsByFormat.set(format, (exportsByFormat.get(format) || 0) + 1);
      }
    }
    // Documentation events
    else if (event.event_type === 'documentation') {
      if (event.event_action === 'view') {
        totalDocViews++;
        activity.docViews++;
        const slug = event.metadata?.slug as string;
        const docType = event.metadata?.docType as string;
        if (slug) {
          const key = `${docType}:${slug}`;
          const existing = docViews.get(key) || { docType, views: 0 };
          existing.views++;
          docViews.set(key, existing);
        }
      }
    }
    // Builder events
    else if (event.event_type === 'builder' || event.event_type === 'adf_builder') {
      totalBuilderActions++;
      activity.builderActions++;
      const builderType = event.event_type === 'adf_builder' ? 'ADF Builder' : 'API Standards Builder';
      builderActions.set(builderType, (builderActions.get(builderType) || 0) + 1);
    }
    // Search events
    else if (event.event_type === 'search') {
      totalSearches++;
      const term = (event.metadata?.term as string) || '';
      if (term) {
        searchTerms.set(term, (searchTerms.get(term) || 0) + 1);
      }
    } 
    // API events
    else if (event.event_type === 'api') {
      const endpoint = event.event_action || 'unknown';
      const existing = apiByEndpoint.get(endpoint) || { total: 0, success: 0 };
      existing.total++;
      const statusCode = event.metadata?.statusCode as number;
      if (statusCode && statusCode >= 200 && statusCode < 400) {
        existing.success++;
      }
      apiByEndpoint.set(endpoint, existing);
    }

    activityByDate.set(dateKey, activity);
  }

  // Convert to arrays and sort
  const topViewedStandards: StandardViewStat[] = Array.from(viewsByStandard.entries())
    .map(([slug, views]) => ({
      slug,
      title: standardsMap.get(slug) || slug,
      views,
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);

  const topValidatedStandards: ValidationStat[] = Array.from(validationsByStandard.entries())
    .map(([slug, stats]) => ({
      slug,
      title: standardsMap.get(slug) || slug,
      validations: stats.total,
      successRate: stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 0,
    }))
    .sort((a, b) => b.validations - a.validations)
    .slice(0, 10);

  const exportsByFormatArray: ExportFormatStat[] = Array.from(exportsByFormat.entries())
    .map(([format, count]) => ({ format, count }))
    .sort((a, b) => b.count - a.count);

  const topSearchTermsArray: SearchTermStat[] = Array.from(searchTerms.entries())
    .map(([term, count]) => ({ term, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  const apiUsageByEndpointArray: ApiUsageStat[] = Array.from(apiByEndpoint.entries())
    .map(([endpoint, stats]) => ({
      endpoint,
      count: stats.total,
      successRate: stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count);

  const dailyActivityArray: DailyActivityStat[] = Array.from(activityByDate.entries())
    .map(([date, stats]) => ({
      date,
      ...stats,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const topAdfElements: AdfElementStat[] = Array.from(adfElementViews.entries())
    .map(([slug, views]) => ({ slug, views }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);

  const topDocArticles: DocViewStat[] = Array.from(docViews.entries())
    .map(([key, data]) => ({
      slug: key.split(':')[1] || key,
      docType: data.docType,
      views: data.views,
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);

  const builderUsageArray: BuilderUsageStat[] = Array.from(builderActions.entries())
    .map(([builder, actions]) => ({ builder, actions }))
    .sort((a, b) => b.actions - a.actions);

  return {
    summary: { 
      totalViews, 
      totalValidations, 
      totalExports, 
      totalSearches,
      totalAdfViews,
      totalDocViews,
      totalBuilderActions,
      uniqueSessions: uniqueSessions.size,
    },
    topViewedStandards,
    topValidatedStandards,
    exportsByFormat: exportsByFormatArray,
    topSearchTerms: topSearchTermsArray,
    apiUsageByEndpoint: apiUsageByEndpointArray,
    dailyActivity: dailyActivityArray,
    topAdfElements,
    topDocArticles,
    builderUsage: builderUsageArray,
  };
}
